// Lightweight frontend-only IPA approximator (pure JS, no native libs)
// Produces compact IPA (no spaces between phonemes) wrapped in slashes, e.g. /həloʊ/

// A small dictionary for high-frequency words to improve accuracy
const DICTIONARY = {
  hello: 'həˈloʊ',
  world: 'wɜːrld',
  apple: 'ˈæpəl',
  testing: 'ˈtɛstɪŋ',
  conversion: 'kənˈvɜːrʒən',
  like: 'laɪk',
  ipa: 'aɪ piː eɪ',
  the: 'ðə',
  a: 'ə',
  an: 'æn',
  i: 'aɪ',
  you: 'juː',
  we: 'wiː',
  they: 'ðeɪ',
  he: 'hiː',
  she: 'ʃiː',
  it: 'ɪt',
  to: 'tuː',
  of: 'ɒv',
  and: 'ænd',
  is: 'ɪz',
  are: 'ɑr'
};

// Add more high-frequency words and helpful corrections
Object.assign(DICTIONARY, {
  so: 'soʊ',
  go: 'goʊ',
  curious: 'ˈkjʊəriəs',
  about: 'əˈbaʊt',
  cemetery: 'ˈsɛmɪˌtɛri',
  holiday: 'ˈhɑlədeɪ',
  happening: 'ˈhæpənɪŋ',
});

// Add a few common function words and plural forms to improve accuracy
Object.assign(DICTIONARY, {
  their: 'ðeə',
  there: 'ðeər',
  that: 'ðæt',
  cemeteries: 'ˈsɛmətriz'
});

// Add common multi-word / irregular pronunciations to improve accuracy for phrases
Object.assign(DICTIONARY, {
  arrive: 'əˈraɪv',
  guatemala: 'ˌɡwɑtəˈmɑlə',
  november: 'noʊˈvɛmbɚ',
  day: 'deɪ',
  dead: 'dɛd',
  on: 'ɑn',
  in: 'ɪn',
});

// Grapheme -> IPA replacement rules (ordered longest-first)
const RULES = [
  ['tion', 'ʃən'], ['sion', 'ʒən'], ['ture', 'tʃər'], ['ough', 'ɔː'], ['eigh', 'eɪ'], ['igh', 'aɪ'],
  ['ph', 'f'], ['ch', 'tʃ'], ['sh', 'ʃ'], ['th', 'θ'], ['ng', 'ŋ'], ['nk', 'ŋk'],
  ['ee', 'iː'], ['ea', 'iː'], ['oo', 'uː'], ['ow', 'aʊ'], ['ou', 'aʊ'], ['oi', 'ɔɪ'], ['oy', 'ɔɪ'], ['ai', 'eɪ'], ['ay', 'eɪ'],
  ['au', 'ɔː'], ['qu', 'kw'], ['kn', 'n'], ['wr', 'r'], ['wh', 'w']
];

const sanitize = (s) => String(s || '').toLowerCase().replace(/[^a-z\s']/g, '').trim();

// Apply rules then map remaining single letters to conservative IPA equivalents.
function applyRules(word) {
  if (!word) return '';
  let w = word.toLowerCase();

  // Apply ordered multi-letter rules first
  for (const [pat, rep] of RULES) {
    w = w.split(pat).join(rep);
  }

  // Handle final silent 'e' heuristics: 'bake' -> beɪk (approx)
  w = w.replace(/([aeiou])e$/g, (m, a) => {
    if (a === 'a') return 'eɪ';
    if (a === 'i') return 'aɪ';
    return 'ə';
  });

  // Map remaining single graphemes to IPA (conservative)
  const charMap = {
    a: 'æ', e: 'ɛ', i: 'ɪ', o: 'ɒ', u: 'ʌ',
    b: 'b', c: 'k', d: 'd', f: 'f', g: 'g', h: 'h', j: 'dʒ', k: 'k', l: 'l', m: 'm', n: 'n',
    p: 'p', q: 'k', r: 'ɹ', s: 's', t: 't', v: 'v', w: 'w', x: 'ks', y: 'j', z: 'z', '\'': "'"
  };

  let out = '';
  for (let i = 0; i < w.length; i++) {
    const ch = w[i];
    // if it's already an IPA symbol (non-ascii) or part of previous replacements, keep as-is
    if (/[\u0250-\u02AF\u02B0-\u02FF\u0300-\u036F\u00A0-\u00FF]/.test(ch) || ch === 'ˈ' || ch === 'ˌ' || ch === ' ' || ch === 'ː') {
      out += ch;
      continue;
    }
    out += (charMap[ch] !== undefined) ? charMap[ch] : ch;
  }

  // Normalize spaces inside the result (ensure single spaces between word-level IPA units)
  out = out.replace(/\s+/g, ' ').trim();
  return out;
}

// Convert a full text line to spaced IPA wrapped in slashes
function textToIPAPure(text) {
  if (!text) return '';
  // Tokenize preserving words, numbers and punctuation
  const raw = String(text || '');
  const tokenRe = /[A-Za-z']+|[0-9]+|[.,!?;:()-]/g;
  const tokens = raw.match(tokenRe) || [];
  if (tokens.length === 0) return '';

  const parts = [];
  for (const tk of tokens) {
    // punctuation
    if (/^[.,!?;:()\-]$/.test(tk)) {
      // attach to previous part if exists, else keep as standalone
      if (parts.length > 0) {
        parts[parts.length - 1] = parts[parts.length - 1] + tk;
      } else {
        parts.push(tk);
      }
      continue;
    }

    // numbers: keep as-is
    if (/^[0-9]+$/.test(tk)) {
      parts.push(tk);
      continue;
    }

    const low = tk.toLowerCase();

    // Handle contractions with apostrophes (e.g. I'm, you're, don't)
    if (low.includes("'")) {
      const subparts = low.split("'").filter(Boolean);
      const mapped = subparts.map(sp => {
        if (DICTIONARY[sp]) return DICTIONARY[sp].replace(/\s+/g, '');
        return applyRules(sp);
      }).filter(Boolean).join('');
      parts.push(mapped);
      continue;
    }

    if (DICTIONARY[low]) {
      // remove internal spaces inside dictionary IPA entries
      parts.push(DICTIONARY[low].replace(/\s+/g, ''));
    } else {
      parts.push(applyRules(low));
    }
  }

  // Join with single space between word-level IPA parts
  const out = parts.join(' ').trim();
  return out ? '/' + out + '/' : '';
}

export { textToIPAPure };
